import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

// Shared business info (single source of truth)
export const BUSINESS = {
  name: "ΓΚΑΤΖΗΣ ΑΡΓΥΡΙΟΣ",
  brand: "GKLed",
  afm: "173822071",
  doy: "Ε' Θεσσαλονίκης",
  activity: "Υπηρεσίες Λιανικής Πώλησης Φωτιστικών",
  address: "6ο χλμ. Θεσσαλονίκης – Λαγκαδά, έναντι TITAN",
  city: "Νέα Ευκαρπία, Θεσσαλονίκη",
  postcode: "56429",
  email: "gkledbusiness@gmail.com",
  phone: "+30 699 979 4496",
  instagram: "https://www.instagram.com/gkled_/",
  url: "https://gk-led.gr",
  startDate: "07/05/2026",
};

const LegalShell = ({ title, intro, children, testId }) => (
  <main className="min-h-screen" data-testid={testId}>
    <div className="max-w-3xl mx-auto px-6 md:px-8 py-12 md:py-16">
      <Link
        to="/"
        className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-[#B026FF] mb-8"
        data-testid="legal-back"
      >
        <ArrowLeft size={16} /> Πίσω στην αρχική
      </Link>
      <div className="text-xs tracking-[0.3em] uppercase text-[#B026FF] font-bold mb-3">
        Νομικό
      </div>
      <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter mb-4">
        {title}
      </h1>
      {intro && <p className="text-zinc-400 leading-relaxed mb-8">{intro}</p>}
      <div className="prose-legal">{children}</div>
      <div className="mt-12 pt-6 border-t border-white/5 text-xs text-zinc-500">
        Τελευταία ενημέρωση: 07/05/2026 · {BUSINESS.brand} · ΑΦΜ {BUSINESS.afm}
      </div>
    </div>
    <style>{`
      .prose-legal h2 { font-family: 'Unbounded', sans-serif; font-weight: 800; font-size: 1.3rem; margin-top: 2rem; margin-bottom: 0.75rem; color: #fff; }
      .prose-legal h3 { font-weight: 700; font-size: 1rem; margin-top: 1.5rem; margin-bottom: 0.5rem; color: #fff; }
      .prose-legal p, .prose-legal li { color: #a1a1aa; line-height: 1.7; font-size: 0.95rem; }
      .prose-legal p { margin-bottom: 0.9rem; }
      .prose-legal ul { list-style: disc; padding-left: 1.4rem; margin-bottom: 0.9rem; }
      .prose-legal ul li { margin-bottom: 0.4rem; }
      .prose-legal strong { color: #fff; font-weight: 600; }
      .prose-legal a { color: #B026FF; }
      .prose-legal a:hover { text-decoration: underline; }
    `}</style>
  </main>
);

// ============ TERMS OF SERVICE ============
export const TermsPage = () => (
  <LegalShell
    title="Όροι Χρήσης"
    testId="terms-page"
    intro={`Παρακαλούμε διαβάστε προσεκτικά τους παρακάτω όρους πριν χρησιμοποιήσετε το ${BUSINESS.url}.`}
  >
    <h2>1. Στοιχεία επιχείρησης</h2>
    <p>
      Το ηλεκτρονικό κατάστημα <strong>{BUSINESS.url}</strong> ({BUSINESS.brand}) ανήκει στην ατομική επιχείρηση:
    </p>
    <ul>
      <li><strong>Επωνυμία:</strong> {BUSINESS.name}</li>
      <li><strong>Δραστηριότητα:</strong> {BUSINESS.activity}</li>
      <li><strong>ΑΦΜ:</strong> {BUSINESS.afm}</li>
      <li><strong>ΔΟΥ:</strong> {BUSINESS.doy}</li>
      <li><strong>Έδρα:</strong> {BUSINESS.address}, {BUSINESS.postcode} {BUSINESS.city}</li>
      <li><strong>Email:</strong> {BUSINESS.email}</li>
      <li><strong>Τηλέφωνο:</strong> {BUSINESS.phone}</li>
    </ul>

    <h2>2. Αποδοχή όρων</h2>
    <p>
      Με την επίσκεψη ή/και τη χρήση του {BUSINESS.brand}, αποδέχεστε ανεπιφύλακτα τους παρόντες όρους. Αν δεν συμφωνείτε με κάποιον από αυτούς, παρακαλούμε μην προχωρήσετε σε αγορά.
    </p>

    <h2>3. Προϊόντα & τιμές</h2>
    <p>
      Όλα τα προϊόντα που εμφανίζονται στο site είναι <strong>χειροποίητα flex neon</strong>, κατασκευασμένα κατόπιν παραγγελίας. Οι τιμές αναγράφονται σε <strong>ευρώ (€)</strong> και περιλαμβάνουν τον αναλογούντα Φ.Π.Α. Διατηρούμε το δικαίωμα τροποποίησης τιμών χωρίς προηγούμενη ειδοποίηση.
    </p>

    <h2>4. Παραγγελίες & κατασκευή</h2>
    <p>
      Η <strong>επιβεβαίωση παραγγελίας</strong> γίνεται αυτόματα με email μετά την επιτυχή πληρωμή. Ο χρόνος <strong>κατασκευής + αποστολής είναι 2–5 εργάσιμες ημέρες</strong>. Σε ειδικά custom σχέδια (My Project / Artworks) ενδέχεται να απαιτηθεί επιπλέον χρόνος, για τον οποίο θα ενημερωθείτε εκ των προτέρων.
    </p>

    <h2>5. Πληρωμές</h2>
    <p>
      Δεχόμαστε <strong>πιστωτική/χρεωστική κάρτα</strong> (μέσω Stripe – πλήρως κρυπτογραφημένη συναλλαγή, δεν αποθηκεύουμε στοιχεία κάρτας) και <strong>αντικαταβολή</strong> (επιβάρυνση +3€).
    </p>

    <h2>6. Μεταφορικά</h2>
    <p>
      Τα έξοδα αποστολής βαρύνουν τον πελάτη και διαμορφώνονται <strong>ανάλογα με τον προορισμό</strong> (περιοχή, βάρος δέματος, courier). Το ακριβές ποσό επιβεβαιώνεται κατά την οριστικοποίηση της παραγγελίας.
    </p>

    <h2>7. Πνευματικά δικαιώματα</h2>
    <p>
      Όλα τα κείμενα, σχέδια, λογότυπα και φωτογραφίες του site αποτελούν <strong>πνευματική ιδιοκτησία της {BUSINESS.brand}</strong>. Απαγορεύεται η αναπαραγωγή χωρίς γραπτή άδεια.
    </p>

    <h2>8. Ευθύνη</h2>
    <p>
      Καταβάλλουμε κάθε προσπάθεια για ακρίβεια στις περιγραφές και τις εικόνες των προϊόντων. Ωστόσο, ενδέχεται να υπάρχουν μικρές αποκλίσεις χρώματος λόγω ρυθμίσεων οθόνης. Δεν φέρουμε ευθύνη για βλάβες που προέρχονται από κακή χρήση, εσφαλμένη εγκατάσταση ή φυσική φθορά.
    </p>

    <h2>9. Εφαρμοστέο δίκαιο</h2>
    <p>
      Οι όροι διέπονται από το <strong>Ελληνικό Δίκαιο</strong>. Αρμόδια για κάθε διαφορά είναι τα δικαστήρια Θεσσαλονίκης.
    </p>

    <h2>10. Επικοινωνία</h2>
    <p>
      Για οποιαδήποτε απορία επικοινωνήστε στο <a href={`mailto:${BUSINESS.email}`}>{BUSINESS.email}</a> ή στο <a href={`tel:${BUSINESS.phone.replace(/\s/g, "")}`}>{BUSINESS.phone}</a>.
    </p>
  </LegalShell>
);

// ============ RETURNS POLICY ============
export const ReturnsPage = () => (
  <LegalShell
    title="Πολιτική Επιστροφών"
    testId="returns-page"
    intro="Στο GKLed θέλουμε να είσαι 100% ικανοποιημένος με την παραγγελία σου. Δείτε τη διαδικασία επιστροφής & επιστροφής χρημάτων."
  >
    <h2>1. Δικαίωμα Υπαναχώρησης</h2>
    <p>
      Σύμφωνα με τον Ν. 2251/1994 (όπως τροποποιήθηκε από την Οδηγία 2011/83/ΕΕ), έχετε δικαίωμα να <strong>υπαναχωρήσετε εντός 14 ημερολογιακών ημερών</strong> από την παραλαβή του προϊόντος, χωρίς να αναφέρετε τους λόγους.
    </p>

    <h3>Εξαιρέσεις</h3>
    <p>
      Το δικαίωμα υπαναχώρησης <strong>ΔΕΝ ισχύει</strong> για:
    </p>
    <ul>
      <li>Προϊόντα που έχουν κατασκευαστεί ειδικά για εσάς ή έχουν εξατομικευμένα στοιχεία (π.χ. Artworks, My Project).</li>
      <li>Προϊόντα που έχουν χρησιμοποιηθεί ή φέρουν εμφανείς αλλοιώσεις/φθορές μη οφειλόμενες σε ελάττωμα κατασκευής.</li>
    </ul>

    <h2>2. Διαδικασία επιστροφής</h2>
    <ul>
      <li>Στείλε email στο <a href={`mailto:${BUSINESS.email}`}>{BUSINESS.email}</a> με τον <strong>κωδικό παραγγελίας</strong> και τον λόγο επιστροφής.</li>
      <li>Θα λάβεις απάντηση εντός 1–2 εργάσιμων με τη διεύθυνση επιστροφής.</li>
      <li>Συσκεύασε το προϊόν στην <strong>αρχική του συσκευασία</strong>, σε άριστη κατάσταση.</li>
      <li>Απόστειλε με courier της επιλογής σου. Τα <strong>έξοδα επιστροφής βαρύνουν τον πελάτη</strong> (εκτός αν το προϊόν είναι ελαττωματικό — βλ. §4).</li>
    </ul>

    <h2>3. Επιστροφή χρημάτων</h2>
    <p>
      Μετά την παραλαβή & τον έλεγχο του προϊόντος, η <strong>επιστροφή χρημάτων ολοκληρώνεται εντός 7 εργάσιμων ημερών</strong> στον ίδιο τρόπο πληρωμής που χρησιμοποιήσατε (κάρτα ή τραπεζική κατάθεση για αντικαταβολή).
    </p>

    <h2>4. Ελαττωματικά προϊόντα</h2>
    <p>
      Σε περίπτωση που το προϊόν παραληφθεί <strong>ελαττωματικό ή κατεστραμμένο κατά τη μεταφορά</strong>, επικοινώνησε μαζί μας <strong>εντός 48 ωρών</strong> με φωτογραφίες. Σε αυτή την περίπτωση:
    </p>
    <ul>
      <li>Τα έξοδα επιστροφής και αντικατάστασης βαρύνουν την επιχείρησή μας.</li>
      <li>Σου αποστέλλουμε αντικατάσταση δωρεάν, ή σου επιστρέφουμε πλήρως το αντίτιμο.</li>
    </ul>

    <h2>5. Εγγύηση</h2>
    <p>
      Όλα τα flex neon προϊόντα συνοδεύονται από <strong>εγγύηση 2 ετών</strong> για κατασκευαστικά ελαττώματα του LED ή του μετασχηματιστή (δεν καλύπτει βλάβες από κακή χρήση, υγρασία ή εξωτερικούς παράγοντες).
    </p>

    <h2>6. Επικοινωνία</h2>
    <p>
      Για επιστροφές, εγγυήσεις ή απορίες: <a href={`mailto:${BUSINESS.email}`}>{BUSINESS.email}</a> · <a href={`tel:${BUSINESS.phone.replace(/\s/g, "")}`}>{BUSINESS.phone}</a>
    </p>
  </LegalShell>
);

// ============ PRIVACY POLICY (GDPR) ============
export const PrivacyPage = () => (
  <LegalShell
    title="Πολιτική Απορρήτου"
    testId="privacy-page"
    intro="Η προστασία των προσωπικών σου δεδομένων είναι σημαντική για εμάς. Δείτε πώς συλλέγουμε, χρησιμοποιούμε και προστατεύουμε τα στοιχεία σας."
  >
    <h2>1. Υπεύθυνος Επεξεργασίας</h2>
    <ul>
      <li><strong>Επωνυμία:</strong> {BUSINESS.name} ({BUSINESS.brand})</li>
      <li><strong>ΑΦΜ:</strong> {BUSINESS.afm} · ΔΟΥ: {BUSINESS.doy}</li>
      <li><strong>Έδρα:</strong> {BUSINESS.address}, {BUSINESS.postcode} {BUSINESS.city}</li>
      <li><strong>Email:</strong> <a href={`mailto:${BUSINESS.email}`}>{BUSINESS.email}</a></li>
    </ul>

    <h2>2. Τι δεδομένα συλλέγουμε</h2>
    <p>Συλλέγουμε <strong>ΜΟΝΟ</strong> τα δεδομένα που μας δίνεις εσύ, για την εκτέλεση της παραγγελίας σου:</p>
    <ul>
      <li><strong>Στοιχεία επικοινωνίας:</strong> Ονοματεπώνυμο, email, τηλέφωνο</li>
      <li><strong>Διεύθυνση αποστολής:</strong> Διεύθυνση, πόλη, ΤΚ</li>
      <li><strong>Στοιχεία παραγγελίας:</strong> προϊόντα, ποσά, τρόπος πληρωμής</li>
      <li><strong>Στοιχεία πληρωμής:</strong> Διαχειρίζονται απευθείας από τον πάροχο πληρωμών (Stripe) — <strong>ΔΕΝ αποθηκεύουμε</strong> αριθμούς πιστωτικών καρτών στο σύστημά μας.</li>
    </ul>

    <h2>3. Σκοπός επεξεργασίας</h2>
    <ul>
      <li>Εκτέλεση της παραγγελίας (νομική βάση: σύμβαση)</li>
      <li>Αποστολή email επιβεβαίωσης (νομική βάση: σύμβαση)</li>
      <li>Φορολογική τεκμηρίωση (νομική βάση: νομική υποχρέωση)</li>
      <li>Επικοινωνία σχετικά με την παραγγελία σου (νομική βάση: σύμβαση)</li>
    </ul>

    <h2>4. Με ποιους μοιραζόμαστε τα δεδομένα</h2>
    <p>Τα δεδομένα σου διαβιβάζονται μόνο σε:</p>
    <ul>
      <li><strong>Stripe</strong> — επεξεργασία πληρωμών (privacy: stripe.com/privacy)</li>
      <li><strong>Resend</strong> — αποστολή email επιβεβαίωσης (privacy: resend.com/legal/privacy-policy)</li>
      <li><strong>Εταιρείες courier</strong> — για την αποστολή του δέματος</li>
    </ul>
    <p>
      <strong>ΔΕΝ πουλάμε</strong> και <strong>ΔΕΝ μοιραζόμαστε</strong> τα δεδομένα σου με τρίτους για διαφημιστικούς σκοπούς.
    </p>

    <h2>5. Χρόνος διατήρησης</h2>
    <p>
      Διατηρούμε τα δεδομένα παραγγελίας για όσο διάστημα απαιτεί η <strong>φορολογική νομοθεσία (έως 10 έτη)</strong>. Στοιχεία επικοινωνίας που δεν συνδέονται με παραγγελία διαγράφονται μετά από 12 μήνες.
    </p>

    <h2>6. Τα δικαιώματά σου</h2>
    <p>Σύμφωνα με τον GDPR (Κανονισμός 2016/679), έχεις δικαίωμα να:</p>
    <ul>
      <li>Ζητήσεις <strong>πρόσβαση</strong> στα προσωπικά σου δεδομένα</li>
      <li>Ζητήσεις <strong>διόρθωση</strong> ανακριβών στοιχείων</li>
      <li>Ζητήσεις <strong>διαγραφή</strong> (right to be forgotten), εφόσον δεν αντίκειται σε φορολογική υποχρέωση</li>
      <li>Ζητήσεις <strong>περιορισμό</strong> της επεξεργασίας</li>
      <li>Ζητήσεις <strong>φορητότητα</strong> των δεδομένων σου</li>
      <li>Υποβάλεις <strong>καταγγελία</strong> στην Αρχή Προστασίας Δεδομένων Προσωπικού Χαρακτήρα (<a href="https://www.dpa.gr" target="_blank" rel="noopener noreferrer">dpa.gr</a>)</li>
    </ul>
    <p>Για όλα τα παραπάνω: <a href={`mailto:${BUSINESS.email}`}>{BUSINESS.email}</a></p>

    <h2>7. Cookies</h2>
    <p>
      Χρησιμοποιούμε ελάχιστα <strong>τεχνικά απαραίτητα cookies</strong> για τη λειτουργία του καλαθιού (localStorage). ΔΕΝ χρησιμοποιούμε διαφημιστικά ή tracking cookies τρίτων.
    </p>

    <h2>8. Ασφάλεια</h2>
    <p>
      Όλη η επικοινωνία με το site γίνεται μέσω <strong>HTTPS (SSL κρυπτογράφηση)</strong>. Οι πληρωμές με κάρτα γίνονται απευθείας στα servers του <strong>Stripe</strong> (PCI-DSS Level 1 certified) χωρίς να περνούν από το δικό μας σύστημα.
    </p>

    <h2>9. Τροποποιήσεις</h2>
    <p>
      Διατηρούμε το δικαίωμα τροποποίησης της παρούσας πολιτικής. Κάθε ενημέρωση θα αναρτάται στη σελίδα αυτή.
    </p>
  </LegalShell>
);

export default { TermsPage, ReturnsPage, PrivacyPage, BUSINESS };
